/** Per-side print-file repair. Every displayed side, format and filename comes
 * from the server matrix; successful PUTs survive verification and sibling
 * retries.
 *
 * Freshness (BLU-663 review): the matrix is NOT polled. `getPrintFileIssueTargets`
 * only probes cloud storage when `includeDownloadUrls` is true, so a 15s poll
 * meant a HEAD per target per operator every 15 seconds for a dialog nobody was
 * looking at. This form asks for the probe once — it needs the existing-file
 * link — and refreshes on the `print_file_bridge_progress` / resolved SSE events
 * (`usePrintFileEvents` invalidates this exact query), on its own uploads, and
 * on the explicit "Refresh file status" control.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { DialogFooter } from '@/components/ui/dialog';
import { trpc } from '@/lib/trpc';
import { getUserMessage } from '@/lib/trpc-error-utils';
import { playSuccessBeep } from '@/lib/sounds';
import { PrintFileTargetRow } from './PrintFileTargetRow';
import type { PrintFileTargetPhase } from '../printFileTargetState';
import { printFileDetailMessage } from '../printFileDetail';
import { uploadTargetKey } from '../hooks/usePrintFileIssueBulkUpload';
import { PlatenVariantUpdatesLink } from './PlatenVariantUpdatesLink';
import { platenVariantRouteMessage, usePlatenVariantRoute } from '../hooks/usePlatenVariantRoute';

interface Props {
  issueId: string;
  /** Toast + chime on the resolve. Omitted where the host already announces
   *  it (the wrong-artwork flow uses its own copy). */
  resolvedToastKey?: string;
  /** Embedded under a metadata form: the host owns Cancel, so no footer. */
  embedded?: boolean;
  /** order_items.product_id, so the Product updates link opens this product. */
  shopifyProductId?: number | string | null;
  /** The issue row's org (the product's owner): a 3PL owner viewing a
   *  client's issue gets "ask the client", never a link or an upload. */
  issueOrganizationId?: string | null;
  onResolved: () => void;
  onCancel?: () => void;
}

interface UploadState {
  file: File | null;
  uploaded?: boolean;
  phase?: PrintFileTargetPhase;
  message?: string;
}

export function PrintFileSelfHealForm({
  issueId,
  resolvedToastKey = 'issues.printFiles.upload.healed',
  embedded = false,
  shopifyProductId,
  issueOrganizationId,
  onResolved,
  onCancel,
}: Props) {
  const { t } = useTranslation('production');
  const platenRoute = usePlatenVariantRoute(issueOrganizationId, shopifyProductId);
  const utils = trpc.useUtils();
  // `includeDownloadUrls` drives the server's storage probe: without it every
  // target comes back `unknown` and the "View existing file" link is absent.
  // The dialog is the only place that needs both, and it only mounts when open.
  const query = trpc.printAgentAdmin.getPrintFileIssueTargets.useQuery(
    { issueId, includeDownloadUrls: true },
    { staleTime: 0 },
  );
  const upload = trpc.printAgentAdmin.uploadReplacementFile.useMutation();
  const verify = trpc.printAgentAdmin.resolveAfterUpload.useMutation();
  const [uploads, setUploads] = useState<Record<string, UploadState>>({});
  // Announced (never silently swallowed) when the server re-composes the
  // association under the operator: their file choices no longer apply.
  const [revisionChanged, setRevisionChanged] = useState(false);
  const uploadsRef = useRef(uploads);
  const resolvedRef = useRef(false);
  const seenRevision = useRef<string | null | undefined>(undefined);
  const revision = query.data?.targetRevision;
  const currentRevision = useRef(revision);
  currentRevision.current = revision;
  const currentIssue = useRef(issueId);
  currentIssue.current = issueId;
  const isCurrent = () => currentRevision.current === revision && currentIssue.current === issueId;
  const patch = (key: string, value: Partial<UploadState>) => {
    uploadsRef.current = {
      ...uploadsRef.current,
      [key]: { file: null, ...uploadsRef.current[key], ...value },
    };
    setUploads(uploadsRef.current);
  };
  const finish = () => {
    if (resolvedRef.current) return;
    resolvedRef.current = true;
    // Visual twin first (PRODUCTION_DESIGN §5.2 — never audio-only), then the
    // chime: the dialog closes on `onResolved`, so without this the only
    // feedback for a successful repair was the row vanishing from the list.
    toast.success(t(resolvedToastKey));
    playSuccessBeep();
    onResolved();
  };
  useEffect(() => {
    // A new issue, or a re-composed association: cached files and PUT proofs
    // belong to targets that no longer exist. Tell the operator rather than
    // blanking the form under them.
    const previous = seenRevision.current;
    seenRevision.current = revision;
    setRevisionChanged(
      previous !== undefined && previous !== null && revision != null && previous !== revision,
    );
    uploadsRef.current = {};
    setUploads({});
    resolvedRef.current = false;
  }, [issueId, revision]);
  useEffect(() => {
    if (query.data?.resolved) finish();
    // The backend's aggregate resolved state is the sole close signal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issueId, query.data?.resolved]);

  const targets = query.data?.targets ?? [];
  const sides = [...new Set(targets.map((target) => target.side))];
  const busy = Object.values(uploads).some(
    (state) => state.phase === 'uploading' || state.phase === 'verifying',
  );
  const blocked =
    Boolean(query.data?.blockedReason) ||
    query.data?.canResolve === false ||
    query.data?.platenVariantFileNeeded === true;

  const submit = async (target: (typeof targets)[number]) => {
    if (!revision || !query.data?.kind || query.data.blockedReason || target.locked) return;
    const key = uploadTargetKey(issueId, revision, target.side, target.format);
    const state = uploadsRef.current[key];
    if (!state?.file || state.phase === 'uploading' || state.phase === 'verifying') return;
    const file = state.file;
    patch(key, { phase: 'uploading', message: undefined });
    try {
      if (!file.name.toLowerCase().endsWith(`.${target.format}`)) {
        throw new Error(
          t('issues.printFiles.upload.extensionRequired', { format: target.format.toUpperCase() }),
        );
      }
      const identity = {
        issueId,
        targetRevision: revision,
        side: target.side,
        uploadedFormat: target.format,
      };
      if (!state.uploaded) {
        const result = await upload.mutateAsync({ ...identity, kind: query.data.kind });
        if (result.targetRevision !== revision || !isCurrent()) {
          throw new Error(t('issues.printFiles.upload.targetsChanged'));
        }
        const put = await fetch(result.uploadUrl, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/octet-stream' },
          body: file,
        });
        if (!put.ok) throw new Error(t('issues.printFiles.upload.putFailed', { status: put.status }));
        patch(key, { uploaded: true });
      }
      if (isCurrent()) patch(key, { phase: 'verifying' });
      const result = await verify.mutateAsync(identity);
      if (!isCurrent()) return;
      if (result.resolved) {
        finish();
        return;
      }
      if ('targetVerified' in result && result.targetVerified) {
        patch(key, {
          phase: 'verified',
          message: t('issues.printFiles.upload.targetVerified'),
        });
      } else {
        patch(key, {
          phase: 'error',
          message:
            printFileDetailMessage(t, result) ?? t('issues.printFiles.upload.errorGeneric'),
        });
      }
    } catch (error) {
      if (isCurrent()) patch(key, { phase: 'error', message: getUserMessage(error) });
    } finally {
      // Never strand a row mid-flight: every return path above settles the
      // phase, but a future one that doesn't must not leave a permanent
      // spinner on an idle row.
      const phase = isCurrent() ? uploadsRef.current[key]?.phase : undefined;
      if (phase === 'uploading' || phase === 'verifying') patch(key, { phase: 'idle' });
      void utils.productionOrders.issues.list.invalidate();
      void utils.productionOrders.issues.getCounts.invalidate();
      void query.refetch();
    }
  };

  return (
    <div className="space-y-4 py-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-lg font-semibold">{t('issues.printFiles.upload.filesHeading')}</h3>
        <Button
          className="h-12 gap-2 text-base"
          variant="outline"
          onClick={() => void query.refetch()}
          disabled={busy || query.isFetching}
        >
          <RefreshCw
            className={`h-5 w-5 ${query.isFetching ? 'motion-safe:animate-spin' : ''}`}
            aria-hidden="true"
          />
          {t('issues.printFiles.upload.refreshStatus')}
        </Button>
      </div>

      {/* ONE polite live region per surface: every non-blocking status this
          form can report is written here (PRODUCTION_DESIGN §13). */}
      <div role="status" aria-live="polite" className="space-y-2 empty:hidden">
        {query.isLoading && <p className="text-base">{t('issues.printFiles.upload.loading')}</p>}
        {revisionChanged && (
          <p className="rounded-lg bg-amber-50 p-3 text-base text-amber-900 dark:bg-amber-950 dark:text-amber-100">
            {t('issues.printFiles.upload.revisionChanged')}
          </p>
        )}
        {query.data?.platenVariantFileNeeded && (
          <div
            data-testid="platen-variant-upload-redirect"
            className="space-y-3 rounded-lg bg-amber-50 p-3 text-base text-amber-900 dark:bg-amber-950 dark:text-amber-100"
          >
            <p>{platenVariantRouteMessage(t, platenRoute)}</p>
            {platenRoute.kind === 'upload' && (
              <PlatenVariantUpdatesLink href={platenRoute.href} className="gap-2 text-base" iconClassName="h-5 w-5" />
            )}
          </div>
        )}
        {query.data?.blockedReason && !query.data.platenVariantFileNeeded && (
          <p className="rounded-lg bg-amber-50 p-3 text-base text-amber-900 dark:bg-amber-950 dark:text-amber-100">
            {t('issues.printFiles.upload.metadataRequired')}
          </p>
        )}
        {query.data && !query.data.canResolve && !query.data.blockedReason && !query.data.platenVariantFileNeeded && (
          <p className="text-base text-amber-700 dark:text-amber-300">
            {t('issues.printFiles.upload.fleetUnknown')}
          </p>
        )}
      </div>

      {query.isError && (
        <Alert variant="destructive" role="alert">
          <AlertCircle className="h-5 w-5" aria-hidden="true" />
          <AlertDescription className="text-base">{getUserMessage(query.error)}</AlertDescription>
        </Alert>
      )}

      {sides.map((side) => {
        const headingId = `print-file-side-${side}`;
        return (
          <section
            key={side}
            aria-labelledby={headingId}
            className="space-y-3 rounded-lg border p-4"
          >
            <h4 id={headingId} className="text-lg font-semibold">
              {t(`issues.printFiles.printData.${side}Title`)}
            </h4>
            {targets
              .filter((target) => target.side === side)
              .map((target) => {
                const key = uploadTargetKey(issueId, revision ?? '', side, target.format);
                const state = uploads[key];
                return (
                  <PrintFileTargetRow
                    key={key}
                    target={target}
                    sideLabel={t(`issues.printFiles.printData.${side}Title`)}
                    file={state?.file ?? null}
                    phase={state?.phase ?? 'idle'}
                    message={state?.message}
                    uploaded={state?.uploaded}
                    disabled={blocked}
                    idSuffix={key}
                    testId={`target-${side}-${target.format}`}
                    onFileChange={(file) =>
                      patch(key, { file, uploaded: false, phase: 'idle', message: undefined })
                    }
                    onSubmit={() => void submit(target)}
                  />
                );
              })}
          </section>
        );
      })}

      {!embedded && onCancel && (
        <DialogFooter className="gap-2">
          <Button className="h-12 text-base" variant="outline" onClick={onCancel} disabled={busy}>
            {t('issues.resolve.cancelBtn')}
          </Button>
        </DialogFooter>
      )}
    </div>
  );
}
